export const PROGRAMMATION_GROUP_DEFAULTABLE_OPTION_KEYS = [
    'sections',
    '_3d',
    'break',
    'version',
    'format',
    'support',
    'ignore_on_maccsbox',
    'disable_votes',
    'distributor_flat_fee',
    'booking_mode',
    'booking_note',
];

export function empty_group_options() {
    return {
        sections: [],
        _3d: false,
        break: false,
        version: '',
        format: '',
        support: '',
        ignore_on_maccsbox: false,
        disable_votes: false,
        distributor_flat_fee: 0,
        booking_mode: '',
        booking_note: {},
    };
}

export function slot_overrides_defaults(slot) {
    return !!slot?.override_defaults;
}

/**
 * Drop fields that must come from the group when the slot does not override.
 * Stale slot.description made empty group descriptions reappear after reload
 * (form seed from first non-empty slot).
 *
 * @param {object|null|undefined} slot
 * @returns {object}
 */
export function strip_inherited_slot_fields(slot) {
    const next = { ...(slot || {}), override_defaults: false };
    delete next.description;
    delete next.pricinglist_id;
    delete next.bucketlist_id;
    delete next.tasks;
    delete next.events;
    const options = { ...(next.options || {}) };
    PROGRAMMATION_GROUP_DEFAULTABLE_OPTION_KEYS.forEach((key) => {
        delete options[key];
    });
    next.options = options;
    return next;
}

function clone_json(value, fallback) {
    if (value == null) {
        return fallback;
    }
    return JSON.parse(JSON.stringify(value));
}

function pick_defaultable_options(options) {
    const source = options || {};
    const picked = {};
    PROGRAMMATION_GROUP_DEFAULTABLE_OPTION_KEYS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            picked[key] = source[key];
        }
    });
    return picked;
}

export function merge_event_audio_overrides(group_events, slot_events) {
    const group_list = Array.isArray(group_events) ? group_events : [];
    const slot_list = Array.isArray(slot_events) ? slot_events : [];
    const slot_by_id = {};
    slot_list.forEach((event) => {
        const id = event?.id ?? event?.event_id;
        if (id != null && id !== '') {
            slot_by_id[String(id)] = event;
        }
    });
    return group_list.map((event) => {
        const id = event?.id ?? event?.event_id;
        if (id == null || id === '' || !slot_by_id[String(id)]) {
            return event;
        }
        const overlay = slot_by_id[String(id)];
        const next = { ...event };
        if (Object.prototype.hasOwnProperty.call(overlay, 'audio')) {
            next.audio = overlay.audio;
        }
        if (Object.prototype.hasOwnProperty.call(overlay, 'subtitles')) {
            next.subtitles = overlay.subtitles;
        }
        return next;
    });
}

/**
 * Resolve displayed / effective slot fields from group defaults + optional override.
 *
 * @param {object|null|undefined} group
 * @param {object|null|undefined} slot
 * @returns {object}
 */
export function resolve_slot_for_display(group, slot) {
    const override = slot_overrides_defaults(slot);
    const group_options = group?.options || {};
    const slot_options = slot?.options || {};
    const empty = empty_group_options();
    const options = { ...slot_options };

    PROGRAMMATION_GROUP_DEFAULTABLE_OPTION_KEYS.forEach((key) => {
        if (override && Object.prototype.hasOwnProperty.call(slot_options, key)) {
            options[key] = slot_options[key];
        } else if (Object.prototype.hasOwnProperty.call(group_options, key)) {
            options[key] = group_options[key];
        } else {
            options[key] = empty[key];
        }
    });

    return {
        ...(slot || {}),
        options,
        description: override
            ? (slot?.description ?? group?.description ?? {})
            : (group?.description ?? {}),
        pricinglist_id: override
            ? (slot?.pricinglist_id ?? group?.pricinglist_id)
            : group?.pricinglist_id,
        bucketlist_id: override
            ? (slot?.bucketlist_id ?? group?.bucketlist_id)
            : group?.bucketlist_id,
        tasks: override
            ? (slot?.tasks ?? group?.tasks ?? [])
            : (group?.tasks || []),
        events: override
            ? merge_event_audio_overrides(group?.events, slot?.events)
            : (group?.events || []),
    };
}

export function seed_slot_override_from_group(group, slot) {
    const resolved = resolve_slot_for_display(group, {
        ...(slot || {}),
        override_defaults: false,
    });
    return {
        ...(slot || {}),
        override_defaults: true,
        description: clone_json(resolved.description, {}),
        pricinglist_id: resolved.pricinglist_id,
        bucketlist_id: resolved.bucketlist_id,
        tasks: clone_json(resolved.tasks, []),
        options: {
            ...(slot?.options || {}),
            ...pick_defaultable_options(resolved.options),
        },
        events: clone_json(resolved.events, []),
    };
}

export function copy_slot_override_fields(source, target) {
    return {
        ...(target || {}),
        override_defaults: slot_overrides_defaults(source),
        description: clone_json(source?.description, {}),
        pricinglist_id: source?.pricinglist_id,
        bucketlist_id: source?.bucketlist_id,
        tasks: clone_json(source?.tasks, []),
        options: {
            ...(target?.options || {}),
            ...pick_defaultable_options(source?.options),
        },
        events: clone_json(source?.events, []),
    };
}

export function slot_ignores_maccsbox(group, slot) {
    return !!resolve_slot_for_display(group, slot)?.options?.ignore_on_maccsbox;
}
